import React from 'react';
import { Shield, Zap, Users, TrendingUp, CheckCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const AISecuritySection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-purple-100 text-purple-700">
            AI-Powered Development
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Delivering Excellence Through
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500"> AI + Human Expertise</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            We combine cutting-edge AI automation with experienced human developers to deliver projects 
            on time, within budget, with superior quality.
          </p>
        </div>

        {/* AI + Human Collaboration */}
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center mb-16">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              The Power of AI + Human Collaboration
            </h3>
            <p className="text-slate-600 mb-6">
              We don't replace human developers with AI - we empower them. Our unique approach combines:
            </p>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Zap className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">AI Automation</h4>
                  <p className="text-sm text-slate-600">Handles code generation, testing, documentation, and security scans automatically</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Human Expertise</h4>
                  <p className="text-sm text-slate-600">Strategic decisions, creative solutions, and quality assurance by experienced developers</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">95% Success Rate</h4>
                  <p className="text-sm text-slate-600">Proven track record of delivering projects on time and within budget</p>
                </div>
              </div>
            </div>
          </div>
          <div className="relative mt-8 md:mt-0">
            <img 
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800" 
              alt="AI and Human Collaboration" 
              className="rounded-2xl shadow-2xl w-full"
            />
            <div className="hidden md:block absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border border-slate-100">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">60%</p>
                  <p className="text-sm text-slate-600">Faster Delivery</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Security Tools */}
        <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 md:p-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              AI-Powered Security & Quality Assurance
            </h3>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our AI security agents continuously monitor and protect your application throughout the development lifecycle
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Automated Code Review</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  AI agents review every line of code for bugs, vulnerabilities, and best practices before human review
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Real-time Threat Detection</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  Continuous scanning for security threats, SQL injection, XSS, and OWASP Top 10 vulnerabilities
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Intelligent Testing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  AI generates and runs comprehensive test cases, achieving 95%+ code coverage automatically
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-0 sm:space-x-8 bg-white px-6 sm:px-8 py-4 rounded-2xl sm:rounded-full shadow-lg">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-blue-600">95%</p>
                <p className="text-xs sm:text-sm text-slate-600">Success Rate</p>
              </div>
              <div className="hidden sm:block w-px h-12 bg-slate-200"></div>
              <div className="block sm:hidden w-16 h-px bg-slate-200"></div>
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-green-600">60%</p>
                <p className="text-xs sm:text-sm text-slate-600">Faster</p>
              </div>
              <div className="hidden sm:block w-px h-12 bg-slate-200"></div>
              <div className="block sm:hidden w-16 h-px bg-slate-200"></div>
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-purple-600">100%</p>
                <p className="text-xs sm:text-sm text-slate-600">Secure</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AISecuritySection;
